import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "denied", label: "Denied" },
];

export default function ResetRequestsTab() {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/reset-requests`, { params: { status, limit: 100 } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [status]);

  async function resolve(id, action) {
    setBusy(id + action);
    try {
      await api.post(`/reset-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Reset approved — employee can retry" : "Request denied");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  }

  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-bold text-slate-900">Assessment Reset Requests</div>
            <p className="text-xs text-slate-500">Employees can request a reset when Pre/Post-Test attempts are exhausted.</p>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={status} onValueChange={setStatus}>
              <TabsList className="bg-slate-100">
                {STATUS_TABS.map((t) => (
                  <TabsTrigger key={t.v} value={t.v} data-testid={`resets-status-${t.v}`}>{t.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="resets-refresh">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reset-requests-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="py-3 px-4">Requested</th>
                <th className="py-3 px-4">Employee</th>
                <th className="py-3 px-4">Training</th>
                <th className="py-3 px-4">Phase</th>
                <th className="py-3 px-4">Reason</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.data.length === 0 && (
                <tr><td colSpan="6" className="py-10 text-center text-slate-400">No {status} requests.</td></tr>
              )}
              {data.data.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-3 px-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <div className="font-bold text-slate-900">{r.emp_name}</div>
                    <div className="text-xs text-slate-400">{r.emp_id}</div>
                  </td>
                  <td className="py-3 px-4">{r.questionnaire_title}</td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary" className={r.phase === "pre" ? "bg-sky-100 text-sky-700 capitalize" : "bg-violet-100 text-violet-700 capitalize"}>{r.phase}-Test</Badge>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600 max-w-[260px]">
                    <div className="truncate" title={r.reason}>{r.reason || <span className="text-slate-300">—</span>}</div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {status === "pending" ? (
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => resolve(r.id, "deny")} disabled={busy === r.id + "deny"} data-testid={`reset-deny-${r.id}`}>
                          {busy === r.id + "deny" ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><X className="h-3.5 w-3.5 mr-1"/>Deny</>}
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(r.id, "approve")} disabled={busy === r.id + "approve"} data-testid={`reset-approve-${r.id}`}>
                          {busy === r.id + "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><Check className="h-3.5 w-3.5 mr-1"/>Approve</>}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">{r.resolved_at ? new Date(r.resolved_at).toLocaleString() : ""}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
